/** Contratos e validações puros da seleção de estoque: compartilhados sem hooks de autenticação. */
export type StockItemType = 'tracker' | 'chip' | 'kit';
export interface StockRef { type: StockItemType; id: string }
export interface ServiceOrderStockSelection {
  install: StockRef[];
  uninstall: StockRef[];
  /** v1.3.0: itens instalados por engano por esta OS (voltam ao prestador). */
  revert_install?: StockRef[];
  /** v1.3.0: itens retirados por engano por esta OS (voltam ao veículo). */
  revert_uninstall?: StockRef[];
}
/** Item retornado pela RPC oficial, incluindo os dados que justificam sua elegibilidade. */
export interface StockCandidate {
  item_type: StockItemType;
  item_id: string;
  label: string;
  detail: string;
  status: string;
  is_active: boolean;
  eligible: boolean;
  blocked_reason: string | null;
  tracker_imei: string | null;
  tracker_model_name: string | null;
  tracker_status: string | null;
  tracker_is_active: boolean | null;
  chip_iccid: string | null;
  chip_line_number: string | null;
  chip_data_plan_name: string | null;
  chip_status: string | null;
  chip_is_active: boolean | null;
}
export interface StockCandidates {
  movement: 'none' | 'install' | 'uninstall' | 'swap';
  required: boolean;
  has_vehicle: boolean;
  install: StockCandidate[];
  uninstall: StockCandidate[];
  /** v1.3.0: OS em correção — itens já movimentados por ela, ainda no destino. */
  correction?: boolean;
  revert_install?: StockCandidate[];
  revert_uninstall?: StockCandidate[];
  /** v1.5.0 (BR-OS-038): troca da correção — itens hoje no veículo e itens do prestador. */
  correction_remove?: StockCandidate[];
  correction_install?: StockCandidate[];
}

/** Modo de conclusão da correção: manter equipamentos ou corrigi-los. */
export type CorrectionMode = 'keep' | 'fix';

/**
 * BR-OS-035/036/037: valida a conclusão de uma OS em correção.
 * "Manter" nunca exige itens; "Corrigir" exige item desta OS, motivo e o
 * equipamento correto quando o tipo de serviço exige seleção.
 * O servidor (`change_service_order_status`) repete todas as validações.
 */
export function validateCorrectionSelection(
  c: Pick<StockCandidates, 'required'> & Partial<Pick<StockCandidates, 'correction_remove'>>,
  mode: CorrectionMode,
  s: ServiceOrderStockSelection,
  note: string,
): string | null {
  if (mode === 'keep') return null;
  // BR-OS-038: "Corrigir" é troca — instala do prestador e, se o veículo tem item, remove um
  const ri = s.revert_install?.length ?? 0;
  if ((c.correction_remove?.length ?? 0) > 0 && ri === 0) return 'Selecione o equipamento do veículo que será trocado';
  if (s.install.length === 0) return 'Selecione o equipamento correto a instalar';
  if (!note.trim()) return 'Informe o motivo da correção';
  return null;
}

export const STOCK_ITEM_LABEL: Record<StockItemType, string> = { tracker: 'Rastreador', chip: 'Chip', kit: 'Kit' };

/** BR-OS-006 / BR-OS-009: mensagem de bloqueio da conclusão, ou null se a seleção é válida. */
export function validateStockSelection(c: Pick<StockCandidates, 'movement' | 'required'>, s: ServiceOrderStockSelection): string | null {
  if (c.movement === 'none') return null;
  if (c.movement === 'swap' && (s.install.length === 0 || s.uninstall.length === 0)) {
    return 'Na troca, selecione o item instalado e o item retirado';
  }
  if (c.required && s.install.length + s.uninstall.length === 0) return 'Selecione os itens de estoque para concluir';
  return null;
}

/** v1.4.0: mantém só as chaves ("tipo:id") ainda presentes e elegíveis na lista nova. */
export function pruneSelection(
  selected: Set<string>,
  items: { item_type: string; item_id: string; eligible?: boolean }[] | undefined,
  requireEligible = true,
): Set<string> {
  const valid = new Set((items ?? [])
    .filter((i) => !requireEligible || i.eligible !== false)
    .map((i) => `${i.item_type}:${i.item_id}`));
  const next = new Set([...selected].filter((k) => valid.has(k)));
  return next.size === selected.size ? selected : next;
}

